<script lang="ts">
  import {onMount} from "svelte";
  import type {IUser} from "$lib/interfaces/IUserMe";
  import NotificationsIcon from "$lib/icons/NotificationsIcon.svelte";
  import {notificationService} from "$lib/services/notification.service";
  import type {INotification} from "$lib/interfaces/INotification";
  import TimeAgo from "$lib/elements/TimeAgo.svelte";
  import NotificationItem from "$lib/components/Notifications/NotificationItem.svelte";
  import {translate} from "$lib/i18n";

  interface Props {
    localUser: IUser;
  }

  let { localUser }: Props = $props();
  let isOpen = $state(false);
  let notificationRef: HTMLDivElement = $state();
  let newNotifications: INotification[] = $state([]);
  let readNotifications: INotification[] = $state([]);

  function openNotifications() {
    isOpen = !isOpen;
  }

  function handleClickOutside(event: MouseEvent) {
    if (notificationRef && !notificationRef.contains(event.target as Node)) {
      isOpen = false;
    }
  }

  function getNotifications() {
    notificationService.getNotifications().then((response: any) => {
      if (response) {
        return response.data;
      }
      throw new Error('Failed to fetch notifications');
    }).then(data => {
      newNotifications = data.data.filter((notification: INotification) => !notification.viewed);
      readNotifications = data.data.filter((notification: INotification) => notification.viewed);
    }).catch(err => {
      console.error(err);
    });
  }

  function markNotificationAsRead(notificationId: number): void {
    applyOptimisticNotificationReadUpdate(notificationId);
    notificationService.markNotificationAsRead(notificationId).then((res) => {
      getNotifications();
    });
  }

  function applyOptimisticNotificationReadUpdate(notificationId: number): void {
    const index = newNotifications.findIndex((notification) => notification.id === notificationId);
    if (index === -1) {
      return;
    }
    const [notificationToMarkAsRead] = newNotifications.splice(index, 1);

    newNotifications = [...newNotifications];
    readNotifications = [...readNotifications, notificationToMarkAsRead];
  }

  onMount(() => {
    getNotifications();
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });
</script>

<div class="relative font-poppins" bind:this={notificationRef}>
  <button
          class="flex items-center justify-center w-10 h-10 rounded-full relative hover:bg-primary-blue/10 transition-colors"
          onclick={() => openNotifications()}
          aria-label="Open notifications"
          title="Notifications"
  >
    <NotificationsIcon hasNotification={newNotifications.length > 0}/>
    {#if newNotifications.length > 0}
      <span class="absolute -top-1 -right-1 bg-primary-fire text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
        {newNotifications.length}
      </span>
    {/if}
  </button>

  {#if isOpen}
    <div class="absolute top-12 right-0 bg-primary-dark w-96 rounded-xl shadow-xl border border-primary-blue/20 overflow-hidden z-50">
      <!-- Header -->
      <div class="p-4 border-b border-primary-blue/20 flex items-center justify-between">
        <h3 class="text-white font-medium">{$translate('Notifications')}</h3>
        {#if newNotifications.length > 0}
          <span class="bg-primary-fire/10 text-primary-fire text-xs px-2 py-1 rounded-full">
            {newNotifications.length} {$translate('new')}
          </span>
        {/if}
      </div>

      <!-- Content -->
      <div class="max-h-[70vh] overflow-y-auto">
        {#if newNotifications.length > 0 || readNotifications.length > 0}
          <!-- New notifications -->
          {#if newNotifications.length > 0}
            <div class="p-3 bg-primary-blue/5">
              <h4 class="text-primary-lightBlue text-xs uppercase font-medium mb-2">{$translate('New')}</h4>
              <div class="space-y-2">
                {#each newNotifications as notification}
                  <NotificationItem
                          {notification}
                          showCloseButton={true}
                          on:closeNotificationPopup={() => isOpen = false}
                          on:click={() => markNotificationAsRead(notification.id)}
                  />
                {/each}
              </div>
            </div>
          {/if}

          <!-- Read notifications -->
          {#if readNotifications.length > 0}
            <div class="p-3">
              <h4 class="text-primary-lightBlue text-xs uppercase font-medium mb-2">{$translate('Earlier')}</h4>
              <div class="space-y-2 opacity-75">
                {#each readNotifications.slice(0, 5) as notification}
                  <NotificationItem
                          {notification}
                          showCloseButton={false}
                          on:closeNotificationPopup={() => isOpen = false}
                  />
                {/each}
              </div>
            </div>
          {/if}
        {:else}
          <!-- Empty state -->
          <div class="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div class="w-12 h-12 bg-primary-blue/10 rounded-full flex items-center justify-center mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-primary-lightBlue opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <p class="text-white">{$translate('No notifications yet')}</p>
            <p class="text-primary-lightBlue text-sm mt-1">{$translate('We\'ll notify you when something important happens')}</p>
          </div>
        {/if}
      </div>
    </div>
  {/if}
</div>